/**
 * Prebuilt automation templates — one-click starting points for common
 * broker workflows. Users pick a template, tweak (name/copy), save.
 *
 * Each template targets exactly ONE of our supported triggers with real,
 * broker-usable copy. Uses merge fields ({{contact.firstName}}, etc.)
 * which will get filled in by the runner in a later milestone; for now
 * the AI-generated / template body is stored verbatim.
 */
import type { AutomationCategory, AutomationWorkflow } from "@/lib/api"

export type AutomationTemplate = {
  id: string
  name: string
  description: string
  category: AutomationCategory
  icon: string // lucide icon key
  workflow: AutomationWorkflow
}

const uid = (n: number) => `s${n}`

// Sensible default send-windows (Australia/Brisbane, local wall-clock hours).
// Templates apply these so brokers don't accidentally SMS a client at 4am.
// The broker can toggle the window off or change hours per step in the builder.
const SMS_WINDOW = { startHour: 8, endHour: 19 }  // 8am - 7pm Brisbane
const EMAIL_WINDOW = { startHour: 7, endHour: 21 } // 7am - 9pm Brisbane

export const AUTOMATION_TEMPLATES: AutomationTemplate[] = [
  // ------------------------------------------------------------
  // TERM END (balloon + end-of-loan reminders)
  //
  // These fire off deal.termEndDate. If the deal has a balloon > 0 the
  // message is about balloon options; if balloon = 0 the message is about
  // upgrading / next steps. Dynamic merge fields ({{deal.termEndIn}},
  // {{deal.termEndDateFormatted}}) keep copy accurate even when the run
  // fires slightly early / late.
  // ------------------------------------------------------------
  {
    id: "term_end_12mo",
    name: "Term end reminder — 12 months out",
    description:
      "Fires 12 months before deal.termEndDate. Good early heads-up for balloon planning or upgrade conversations.",
    category: "balloon",
    icon: "CalendarClock",
    workflow: {
      version: 1,
      trigger: { kind: "term_end", params: { daysBefore: 365 } },
      conditions: [],
      exitConditions: [],
      steps: [
        {
          id: uid(1),
          kind: "send_sms",
          body:
            "Hi {{contact.firstName}}, it's {{broker.firstName}} from Rivercity Finance. Your finance term ends in {{deal.termEndIn}} ({{deal.termEndDateFormatted}}) — good time to review your options. I'll follow up by email.",
          sendWindow: SMS_WINDOW,
        },
        {
          id: uid(2),
          kind: "send_email",
          subject: "Your finance ends in {{deal.termEndIn}} — options to consider",
          body:
            "Hi {{contact.firstName}},\n\nJust a heads up — your finance ends in {{deal.termEndIn}} ({{deal.termEndDateFormatted}}). That's the perfect time to review your options:\n\n• Refinance (extend at a new rate)\n• Upgrade to a newer asset\n• Pay it out\n\nHappy to run through the numbers whenever suits. Just reply to this email or give me a call.\n\nCheers,\n{{broker.firstName}}\nRivercity Finance",
          sendWindow: EMAIL_WINDOW,
        },
        {
          id: uid(3),
          kind: "create_task",
          title: "Term ends in 12 months — reach out",
          priority: "medium",
          description:
            "Follow up on the 12-month term-end reminder. Refi / upgrade / payout conversation.",
        },
      ],
      maxSteps: 50,
    },
  },
  {
    id: "term_end_6mo",
    name: "Term end reminder — 6 months out",
    description:
      "6-month check-in before term end. SMS + email + call task.",
    category: "balloon",
    icon: "CalendarClock",
    workflow: {
      version: 1,
      trigger: { kind: "term_end", params: { daysBefore: 180 } },
      conditions: [],
      exitConditions: [],
      steps: [
        {
          id: uid(1),
          kind: "send_sms",
          body:
            "Hi {{contact.firstName}}, {{broker.firstName}} here from Rivercity Finance. We're {{deal.termEndIn}} out from the end of your finance — quick chat about refi / upgrade options? Reply here or I'll ring you.",
          sendWindow: SMS_WINDOW,
        },
        {
          id: uid(2),
          kind: "send_email",
          subject: "{{deal.termEndIn}} until your finance ends",
          body:
            "Hi {{contact.firstName}},\n\nWe're now {{deal.termEndIn}} out from the end of your finance ({{deal.termEndDateFormatted}}). Let's have a chat about your options — refinance, upgrade, or pay it out.\n\nLet me know what suits.\n\nCheers,\n{{broker.firstName}}",
          sendWindow: EMAIL_WINDOW,
        },
        {
          id: uid(3),
          kind: "create_task",
          title: "Term end 6-month check-in — call the client",
          priority: "high",
        },
      ],
      maxSteps: 50,
    },
  },
  {
    id: "term_end_3mo",
    name: "Term end reminder — 3 months out",
    description:
      "Final email + SMS window before term end. High-priority broker task.",
    category: "balloon",
    icon: "CalendarClock",
    workflow: {
      version: 1,
      trigger: { kind: "term_end", params: { daysBefore: 90 } },
      conditions: [],
      exitConditions: [],
      steps: [
        {
          id: uid(1),
          kind: "send_sms",
          body:
            "Hi {{contact.firstName}}, it's {{broker.firstName}} from Rivercity Finance. Just a heads up your finance term ends in {{deal.termEndIn}} ({{deal.termEndDateFormatted}}). Give me a bell to chat through options.",
          sendWindow: SMS_WINDOW,
        },
        {
          id: uid(2),
          kind: "send_email",
          subject: "Your finance ends in {{deal.termEndIn}} — options to consider",
          body:
            "Hi {{contact.firstName}},\n\nYour finance ends in {{deal.termEndIn}} ({{deal.termEndDateFormatted}}). Here are your options:\n\n1. Refinance (extend the term at a new rate)\n2. Upgrade to a newer asset and refinance the whole thing\n3. Pay it out\n\nLet me know which direction you'd like to take and I'll get the ball rolling.\n\nCheers,\n{{broker.firstName}}",
          sendWindow: EMAIL_WINDOW,
        },
        {
          id: uid(3),
          kind: "create_task",
          title: "Term end 3 months — call client now",
          priority: "high",
        },
      ],
      maxSteps: 50,
    },
  },

  // ------------------------------------------------------------
  // BIRTHDAY
  // ------------------------------------------------------------
  {
    id: "birthday",
    name: "Happy birthday",
    description: "Fires on the contact's birthday. Sends a personal SMS + email.",
    category: "birthday",
    icon: "Cake",
    workflow: {
      version: 1,
      trigger: { kind: "birthday", params: {} },
      conditions: [],
      exitConditions: [],
      steps: [
        {
          id: uid(1),
          kind: "send_sms",
          body:
            "Happy birthday {{contact.firstName}}! Hope you have a cracker of a day. — {{broker.firstName}}, Rivercity Finance",
          sendWindow: SMS_WINDOW,
        },
        {
          id: uid(2),
          kind: "send_email",
          subject: "Happy Birthday {{contact.firstName}}!",
          body:
            "Hi {{contact.firstName}},\n\nJust a quick note to wish you a very happy birthday from all of us at Rivercity Finance. Hope you have a great day!\n\nCheers,\n{{broker.firstName}}",
          sendWindow: EMAIL_WINDOW,
        },
      ],
      maxSteps: 50,
    },
  },

  // ------------------------------------------------------------
  // SETTLEMENT
  // ------------------------------------------------------------
  {
    id: "settlement_thank_you",
    name: "Settlement — thank you & next steps",
    description:
      "Fires the moment a deal moves to Settled. Sends a thank-you, sets a 30-day check-in task.",
    category: "settlement",
    icon: "PartyPopper",
    workflow: {
      version: 1,
      trigger: {
        kind: "deal_stage_changed",
        params: { pipelineStage: "settled" },
      },
      conditions: [],
      exitConditions: [],
      steps: [
        {
          id: uid(1),
          kind: "send_sms",
          body:
            "Congrats {{contact.firstName}}! Your finance has settled. I'll be in touch in 30 days to check in. Any questions in the meantime just reply. — {{broker.firstName}}",
          sendWindow: SMS_WINDOW,
        },
        {
          id: uid(2),
          kind: "send_email",
          subject: "Congrats on your settlement, {{contact.firstName}}!",
          body:
            "Hi {{contact.firstName}},\n\nJust a quick note to say congrats — your finance has officially settled. Everything's in place.\n\nI'll drop you a line in about 30 days to make sure everything is going well. If anything comes up before then, just reply to this email.\n\nAll the best,\n{{broker.firstName}}\nRivercity Finance",
          sendWindow: EMAIL_WINDOW,
        },
        {
          id: uid(3),
          kind: "create_task",
          title: "30-day settlement check-in",
          priority: "medium",
          dueInSeconds: 30 * 24 * 3600,
        },
      ],
      maxSteps: 50,
    },
  },
  {
    id: "settlement_anniversary",
    name: "Settlement anniversary — 12 months",
    description:
      "Fires 12 months after settlement. SMS + email + annual review task.",
    category: "settlement",
    icon: "PartyPopper",
    workflow: {
      version: 1,
      trigger: { kind: "settlement_anniversary", params: { monthsAfter: 12 } },
      conditions: [],
      exitConditions: [],
      steps: [
        {
          id: uid(1),
          kind: "send_sms",
          body:
            "Hi {{contact.firstName}}, {{broker.firstName}} here — hard to believe it's been a year since we settled your finance! Quick check-in soon? I'll send an email with the details.",
          sendWindow: SMS_WINDOW,
        },
        {
          id: uid(2),
          kind: "send_email",
          subject: "Has it really been a year, {{contact.firstName}}?",
          body:
            "Hi {{contact.firstName}},\n\nHard to believe your finance settled 12 months ago! I wanted to check in — has anything changed? New vehicle, business growth, need to look at your rate?\n\nIf you're due for an upgrade or want to see if we can save you money, just reply or give me a call.\n\nCheers,\n{{broker.firstName}}",
          sendWindow: EMAIL_WINDOW,
        },
        {
          id: uid(3),
          kind: "create_task",
          title: "Annual review call",
          priority: "medium",
        },
      ],
      maxSteps: 50,
    },
  },

  // ------------------------------------------------------------
  // LEAD NURTURE
  // ------------------------------------------------------------
  {
    id: "new_lead_5min_sms",
    name: "New lead — instant SMS",
    description:
      "Fires the moment a new contact is created. Sends an SMS within 5 minutes — no time-of-day window (goal is instant response).",
    category: "lead_nurture",
    icon: "Zap",
    workflow: {
      version: 1,
      trigger: { kind: "contact_created", params: {} },
      conditions: [],
      exitConditions: [],
      steps: [
        {
          id: uid(1),
          kind: "wait",
          durationSeconds: 300,
        },
        {
          id: uid(2),
          kind: "send_sms",
          body:
            "Hi {{contact.firstName}}, it's {{broker.firstName}} from Rivercity Finance. Thanks for the enquiry — I'll give you a call shortly. If you'd prefer email or a different time, just reply.",
          // No sendWindow — instant-lead SMS should fire immediately even at 6am.
        },
      ],
      maxSteps: 50,
    },
  },
  {
    id: "new_lead_no_response",
    name: "New lead — 2-day follow-up if no reply",
    description:
      "Fires on new contact. After 2 days: nudge SMS, follow-up email, high-priority broker call task.",
    category: "lead_nurture",
    icon: "Zap",
    workflow: {
      version: 1,
      trigger: { kind: "contact_created", params: {} },
      conditions: [],
      exitConditions: [],
      steps: [
        {
          id: uid(1),
          kind: "wait",
          durationSeconds: 2 * 24 * 3600,
        },
        {
          id: uid(2),
          kind: "send_sms",
          body:
            "Hi {{contact.firstName}}, {{broker.firstName}} from Rivercity Finance following up on your enquiry. Still keen for a chat? Reply here or I'll try a call.",
          sendWindow: SMS_WINDOW,
        },
        {
          id: uid(3),
          kind: "send_email",
          subject: "Just following up, {{contact.firstName}}",
          body:
            "Hi {{contact.firstName}},\n\nJust checking in on your finance enquiry. Happy to answer any questions or run through options — just reply to this email or give me a call.\n\nCheers,\n{{broker.firstName}}\nRivercity Finance",
          sendWindow: EMAIL_WINDOW,
        },
        {
          id: uid(4),
          kind: "create_task",
          title: "Call new lead — 2 days no response",
          priority: "high",
        },
      ],
      maxSteps: 50,
    },
  },

  // ------------------------------------------------------------
  // RE-ENGAGEMENT
  // ------------------------------------------------------------
  {
    id: "lost_deal_90_day_reengage",
    name: "Lost deal — 90-day re-engage",
    description:
      "Fires 90 days after a deal was marked lost. Soft SMS + email check-in.",
    category: "re_engagement",
    icon: "RefreshCw",
    workflow: {
      version: 1,
      trigger: { kind: "days_since_deal_lost", params: { days: 90 } },
      conditions: [],
      exitConditions: [],
      steps: [
        {
          id: uid(1),
          kind: "send_sms",
          body:
            "Hi {{contact.firstName}}, {{broker.firstName}} from Rivercity Finance. Been a few months — anything changed on the finance front? Happy to have another look if you want. No pressure.",
          sendWindow: SMS_WINDOW,
        },
        {
          id: uid(2),
          kind: "send_email",
          subject: "Still keeping an eye on your finance options?",
          body:
            "Hi {{contact.firstName}},\n\nIt's been a few months since we last chatted. Just a quick note that if anything's changed on your end and you'd like to revisit finance options, I'm here.\n\nNo pressure — just wanted to keep the door open.\n\nCheers,\n{{broker.firstName}}",
          sendWindow: EMAIL_WINDOW,
        },
      ],
      maxSteps: 50,
    },
  },
  {
    id: "stale_contact_30day",
    name: "Stale contact — 30-day nudge",
    description:
      "Fires when a contact has had no activity for 30 days. Soft SMS check-in + tag + broker task.",
    category: "re_engagement",
    icon: "RefreshCw",
    workflow: {
      version: 1,
      trigger: { kind: "days_since_last_contact", params: { days: 30 } },
      conditions: [],
      exitConditions: [],
      steps: [
        {
          id: uid(1),
          kind: "add_tag",
          tag: "stale-30d",
        },
        {
          id: uid(2),
          kind: "send_sms",
          body:
            "Hi {{contact.firstName}}, just checking in — anything I can help with on the finance side? — {{broker.firstName}}, Rivercity Finance",
          sendWindow: SMS_WINDOW,
        },
        {
          id: uid(3),
          kind: "create_task",
          title: "Reach out — 30 days no contact",
          priority: "medium",
        },
      ],
      maxSteps: 50,
    },
  },
]

export const CATEGORY_META: Record<
  AutomationCategory,
  { label: string; description: string; color: string; icon: string }
> = {
  lead_nurture: {
    label: "Lead Nurture",
    description: "Automations that engage new leads and prospects",
    color: "bg-emerald-100 text-emerald-700",
    icon: "Zap",
  },
  balloon: {
    label: "Balloon",
    description: "Balloon payment reminders and refi prompts",
    color: "bg-amber-100 text-amber-700",
    icon: "CalendarClock",
  },
  birthday: {
    label: "Birthday",
    description: "Birthday greetings",
    color: "bg-pink-100 text-pink-700",
    icon: "Cake",
  },
  settlement: {
    label: "Settlement",
    description: "Settlement congrats, check-ins, anniversary follow-ups",
    color: "bg-blue-100 text-blue-700",
    icon: "PartyPopper",
  },
  re_engagement: {
    label: "Re-engagement",
    description: "Bring back lost or stale contacts",
    color: "bg-purple-100 text-purple-700",
    icon: "RefreshCw",
  },
  compliance: {
    label: "Compliance",
    description: "Regulatory or audit-driven automations",
    color: "bg-red-100 text-red-700",
    icon: "ShieldCheck",
  },
  internal: {
    label: "Internal",
    description: "Broker/team notifications and admin tasks",
    color: "bg-slate-100 text-slate-700",
    icon: "Users",
  },
  other: {
    label: "Other",
    description: "Uncategorised",
    color: "bg-rc-grey-100 text-rc-grey-700",
    icon: "Sparkles",
  },
}

/**
 * A short human-friendly summary of the trigger for display in cards.
 */
export function formatTrigger(kind: string, params: Record<string, unknown> = {}): string {
  switch (kind) {
    case "contact_created":
      return "When a new contact is created"
    case "contact_updated":
      return "When a contact is updated"
    case "deal_created":
      return "When a deal is created"
    case "deal_stage_changed": {
      const stage = params.pipelineStage
      return stage ? `When a deal moves to ${stage}` : "When a deal stage changes"
    }
    case "deal_assigned":
      return "When a deal is assigned to a broker"
    case "task_completed":
      return "When a task is completed"
    case "task_overdue":
      return "When a task is overdue"
    case "tag_added":
      return "When a tag is added"
    case "manual_enrolment":
      return "Manually enrolled"
    case "birthday":
      return "On the contact's birthday (9am)"
    case "term_end":
      return `${params.daysBefore ?? "?"} days before term end`
    case "balloon_due":
      // Legacy alias — same behaviour as term_end.
      return `${params.daysBefore ?? "?"} days before term end (balloon)`
    case "settlement_anniversary":
      return `${params.monthsAfter ?? "?"} months after settlement`
    case "days_since_last_contact":
      return `${params.days ?? "?"} days since last contact`
    case "days_since_deal_lost":
      return `${params.days ?? "?"} days since deal lost`
    default:
      return kind
  }
}

/**
 * A short human-friendly summary of a step for display in cards.
 */
export function formatStep(step: Record<string, unknown>): string {
  const kind = step.kind as string
  switch (kind) {
    case "wait": {
      const secs = Number(step.durationSeconds ?? 0)
      if (secs >= 86400) return `Wait ${Math.round(secs / 86400)}d`
      if (secs >= 3600) return `Wait ${Math.round(secs / 3600)}h`
      if (secs >= 60) return `Wait ${Math.round(secs / 60)}m`
      return `Wait ${secs}s`
    }
    case "send_sms":
      return "Send SMS"
    case "send_email":
      return "Send email"
    case "create_task":
      return `Create task${step.title ? `: ${step.title}` : ""}`
    case "add_note":
      return "Add note"
    case "add_tag":
      return `Add tag: ${step.tag}`
    case "remove_tag":
      return `Remove tag: ${step.tag}`
    case "move_deal_stage":
      return `Move deal to ${step.pipelineStage}`
    case "assign_broker":
      return "Assign broker"
    case "update_field":
      return `Update ${step.entity}.${step.field}`
    case "if_else":
      return "Branch (if/else)"
    case "exit":
      return "Exit"
    default:
      return kind
  }
}
